import React, { useCallback, useEffect, useState } from "react";
import { GrFormNextLink } from "react-icons/gr";
import { TbRefresh } from "react-icons/tb";
import "../styles/recharge.css";
import AccountLayout from "../layouts/AccountLayout";
import { useNavigate } from "react-router-dom";
import { createTransaction, getBankDepositHistoryList } from "../api/transaction.api";
import { ClipLoader, HashLoader } from "react-spinners";
import ToastNotification, {
  showTopCenterToast,
} from "../components/toasts/ToastNotification";
import { statusText } from "../utils";
import Pagination from "../components/pagination/Pagination";

const BANK_HISTORY_PER_PAGE = 10;

const BankTransfer = () => {
  const [amount, setAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [bankHistory, setBankHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);

  const navigate = useNavigate();

  const fetchBankHistory = useCallback(async (page = historyPage) => {
    setHistoryLoading(true);
    try {
      const res = await getBankDepositHistoryList(BANK_HISTORY_PER_PAGE, page);
      if (res?.success) {
        setBankHistory(res?.data?.transactions || []);
        setHistoryTotalPages(res?.data?.totalPages || 1);
        if (res?.data?.page && res.data.page !== historyPage) {
          setHistoryPage(res.data.page);
        }
      }
    } catch (error) {
      showTopCenterToast(error.message || "Không thể tải lịch sử nạp tiền", "error");
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage]);

  useEffect(() => {
    fetchBankHistory(historyPage);
  }, [historyPage, fetchBankHistory]);

  const handleRecharge = async () => {
    if (amount < 10000) {
      showTopCenterToast("Số tiền nạp tối thiểu phải là 10.000đ", "error");
      return;
    }

    if (amount > 10000000) {
      showTopCenterToast("Số tiền nạp tối đa phải là 10.000.000đ", "error");
      return;
    }

    setLoading(true);
    try {
      const res = await createTransaction(
        amount,
        "empty_gateway",
        "bank_transfer"
      );

      if (res.success) {
        setLoading(false);
        navigate(`/payment/bank-transfer/${res?.data?.transactionId}`);
      }
    } catch (error) {
      setLoading(false);
      showTopCenterToast(error.message, "error");
    }
  };

  return (
    <AccountLayout title="Nạp tiền qua ngân hàng">
      <div className="bank-transfer-page">
        <section className="bank-transfer-hero">
          <p className="bank-transfer-eyebrow">Secure Deposit</p>
          <h2>Nạp tiền qua ngân hàng</h2>
          <p className="bank-transfer-subtitle">
            Tạo giao dịch và nhận mã QR chuyển khoản ngay lập tức.
          </p>
        </section>

        <section className="recharge-container">
          <AmountSelection
            amount={amount}
            setAmount={setAmount}
            loading={loading}
            onRecharge={handleRecharge}
          />
          <BankDepositHistory
            history={bankHistory}
            loading={historyLoading}
            onRefresh={() => fetchBankHistory(historyPage)}
            currentPage={historyPage}
            totalPages={historyTotalPages}
            onPageChange={(page) => setHistoryPage(page)}
          />
        </section>
        <ToastNotification />
      </div>
    </AccountLayout>
  );
};

const AmountSelection = ({
  amount,
  setAmount,
  onRecharge,
  loading,
}) => (
  <>
    <div className="amount-options">
      <p className="deposit-des">Nhập số tiền bạn muốn nạp (10.000đ - 10.000.000đ)</p>
      <input
        type="text"
        value={amount.toLocaleString()}
        onChange={(e) => setAmount(Number(e.target.value.replace(/\D/g, '')))}
        className="user-deposit-amount-input"
        placeholder="Ví dụ: 100000"
      />
    </div>
    <div className="recharge-button-wrapper">
      <button
        onClick={onRecharge}
        disabled={amount === 0}
        className="recharge-button"
      >
        {loading ? (
          <ClipLoader size={20} color="#fff" />
        ) : (
          <GrFormNextLink className="recharge-icon" />
        )}
      </button>
    </div>
  </>
);

const BankDepositHistory = ({ history, loading, onRefresh, currentPage, totalPages, onPageChange }) => {
  const getStatusClass = (status) => {
    const normalized = String(status || "").toLowerCase();
    if (normalized === "success" || normalized === "completed") return "is-success";
    if (normalized === "failed") return "is-failed";
    return "is-pending";
  };

  return (
    <section className="bank-history-section">
      <div className="bank-history-head">
        <div>
          <h3>Lịch sử nạp qua ngân hàng</h3>
          <p>Hiển thị các giao dịch nạp tiền bằng phương thức chuyển khoản ngân hàng.</p>
        </div>
        <button
          type="button"
          className="bank-history-refresh-btn"
          onClick={onRefresh}
          disabled={loading}
        >
          <TbRefresh />
          Làm mới
        </button>
      </div>

      {loading ? (
        <div className="bank-history-loading">
          <HashLoader color="var(--hash-loader-color)" size={26} />
        </div>
      ) : history.length === 0 ? (
        <p className="bank-history-empty">Bạn chưa có giao dịch nạp tiền qua ngân hàng.</p>
      ) : (
        <>
          <div className="bank-history-table-wrap">
            <table className="bank-history-table">
              <thead>
                <tr>
                  <th>Mã giao dịch</th>
                  <th>Số tiền</th>
                  <th>Trạng thái</th>
                  <th>Thời gian</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => {
                  return (
                    <tr key={item.transactionId}>
                      <td>{item.transactionId}</td>
                      <td>
                        <span className="bank-history-amount">
                          {Number(item.amount || 0).toLocaleString("vi-VN")}đ
                        </span>
                      </td>
                      <td>
                        <span className={`bank-history-status ${getStatusClass(item.transactionStatus)}`}>
                          {statusText(item.transactionStatus) || item.transactionStatus || "-"}
                        </span>
                      </td>
                      <td>{new Date(item.createdAt).toLocaleString("vi-VN")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          )}
        </>
      )}
    </section>
  );
};

export default BankTransfer;
